import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  isIframeMode,
  notifyIframeReady,
  notifyApplicationSubmitted,
  useIframeHeightSync
} from '@/utils/iframe';

import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import '@/styles/PhoneInput.css';

import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Input,
  Separator,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react';

import {
  ArrowLeft,
  Briefcase,
  CaretLeft,
  CheckCircle,
  Clock,
  CurrencyDollar,
  DownloadSimple,
  File as FileIcon,
  MapPin,
  UploadSimple,
  Warning,
  X as XIcon
} from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import {
  apiApplyJob,
  apiApplyJobWithVeteransResume,
  apiGetExtractorProviders,
  apiGetJobBoardJob,
  apiJobBoardTranslateVeteransResume,
  GetJobBoardJobResponse,
  JobBoardTranslateVeteransResumeProgress
} from '@/api/services';

import { Avatar } from '@/components/ui/avatar';
import { Field } from '@/components/ui/field';
import RichEditor from '@/components/forms/RichEditor';
import Resume from '@/components/data-display/Resume';

import { exportResumeToDocx } from '@/utils/file';

import {
  AgencyFeature,
  getEmploymentTypeLabel,
  getLlmModelProviderSupportFileTypes,
  getWorkTypeLabel,
  LlmModelProvider,
  PromptKey
} from '@/types/enums';

import { Agency } from '@/types/Agency';
import { Job } from '@/types/Job';
import { CandidateWithJobContext } from '@/types/Candidate';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { ApplicationQuestion } from '@/types/ApplicationQuestion';
import { ApplicationQuestionAnswer } from '@/types/ApplicationQuestionAnswer';

import { getFileSize } from '@/utils';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

import jobBoardBanner from '@/assets/image/job-board-banner.png';

interface Provider {
  analysis_id: string | null;
  analysis_name: PromptKey;
  provider: LlmModelProvider | null;
  loading: boolean;
  error: string | null;
}

const defaultProvider: Provider = {
  analysis_id: null,
  analysis_name: PromptKey.ResumeExtractor,
  provider: null,
  loading: false,
  error: null
};

export function JobDetails() {
  const { agencyId, jobId } = useParams<{ agencyId: string; jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [agency, setAgency] = useState<Agency | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Iframe support
  const inIframeMode = isIframeMode();
  const syncHeight = useIframeHeightSync();

  // Notify parent iframe is ready
  useEffect(() => {
    if (inIframeMode) {
      notifyIframeReady();
    }
  }, [inIframeMode]);

  // Sync height on content changes when in iframe
  useEffect(() => {
    if (inIframeMode) {
      syncHeight();
    }
  }, [inIframeMode, job, isLoading, syncHeight]);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!agencyId || !jobId) {
        setHasError(true);
        setErrorMessage('Unable to load job details. Please try again later.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetchAPI<GetJobBoardJobResponse>({
          request: () => apiGetJobBoardJob({ params: { agency_id: agencyId, job_id: jobId } })
        });

        if (response.success && response.data) {
          const { job, agency } = response.data;
          setJob(job);
          setAgency(agency);
        } else {
          setHasError(true);
          setErrorMessage('Unable to load job details. Please try again later.');
        }
      } catch (error) {
        setHasError(true);
        setErrorMessage('Unable to load job details. Please try again later.');
        console.error('Error fetching job details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobDetails();
  }, [agencyId, jobId]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (hasError || !job) {
    return (
      <Flex bg={inIframeMode ? 'primary.secondary' : 'background.primary'} minH="100vh" w="100%">
        <Box p="24px" maxW="800px" mx="auto">
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="60vh"
            textAlign="center"
            gap="24px"
          >
            <Box color="error.700" display="flex" alignItems="center" justifyContent="center">
              <Warning size={60} />
            </Box>
            <Heading size="xl" color="neutrals.800">
              Unable to Load Job Details
            </Heading>
            <Text fontSize="lg" color="neutrals.600" maxW="800px">
              {errorMessage}
            </Text>
            <Text fontSize="14px" color="neutrals.500" mt="8px">
              Please contact support if this issue persists.
            </Text>
            <Button
              border="1px solid"
              borderColor="neutrals.300"
              color="neutrals.700"
              borderRadius="8px"
              onClick={() => navigate(`/job_board/${agencyId}`)}
              variant="outline"
              size="lg"
              mt="16px"
            >
              <ArrowLeft size={20} />
              <Text ml="4px" fontSize="16px">
                Back to Jobs
              </Text>
            </Button>
          </Flex>
        </Box>
      </Flex>
    );
  }

  const formatSalary = () => {
    if (!job.salary_from && !job.salary_to) return 'Salary not specified';
    if (job.salary_from && job.salary_to) {
      // If salary range is the same, show single value
      if (job.salary_from === job.salary_to) {
        return `${job.currency} ${job.salary_from.toLocaleString()}`;
      }
      return `${
        job.currency
      } ${job.salary_from.toLocaleString()} - ${job.salary_to.toLocaleString()}`;
    }
    if (job.salary_from) {
      return `${job.currency} ${job.salary_from.toLocaleString()}+`;
    }
    return `${job.currency} ${job.salary_to?.toLocaleString()}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Flex
      bg={inIframeMode ? 'primary.secondary' : 'background.primary'}
      minH="100vh"
      w="100%"
      direction="column"
    >
      <Flex direction="column" maxW="1200px" mx="auto">
        {/* Hide banner in iframe mode */}
        {!inIframeMode && (
          <Image src={jobBoardBanner} alt="Job Board Banner" w="100%" h="320px" objectFit="cover" />
        )}
        <Stack p="32px 120px 64px 120px" maxW="1000px" mx="auto">
          {!inIframeMode && (
            <Avatar
              size="2xl"
              w="80px"
              h="80px"
              src={agency?.logo_url}
              name={agency?.name}
              bg="primary.900"
              color={agency?.logo_url ? 'transparent' : 'neutrals.0'}
              fontSize="24px"
              mb="12px"
            />
          )}

          {/* Back Button */}
          <Box as="button" onClick={() => navigate(`/job_board/${agencyId}`)} cursor="pointer">
            <Flex align="center" mb="24px" color="neutrals.600">
              <CaretLeft size={24} />
              <Text ml="8px" fontSize="16px">
                Back to Jobs
              </Text>
            </Flex>
          </Box>

          <Flex justify="space-between" align="center" flexWrap="wrap" gap="16px" mb="24px">
            <Flex align="center" gap="20px">
              <Text fontSize="sm" color="neutrals.500">
                Job ID
              </Text>
              <Text fontSize="md" color="neutrals.900" fontWeight="medium">
                {job.unique_id}
              </Text>
            </Flex>

            <Flex align="center" gap="20px">
              <Text fontSize="sm" color="neutrals.500">
                Posted Date
              </Text>
              <Text fontSize="md" color="neutrals.900" fontWeight="medium">
                {formatDate(job.timestamp.published_at || job.created_at)}
              </Text>
            </Flex>
          </Flex>

          {/* Job Header */}
          <Stack gap="16px" mt="8px" mb="32px">
            <Heading size="2xl" color="neutrals.900">
              {job.job_title}
            </Heading>

            <Flex justify="space-between" flexWrap="wrap" mt="8px" maxW="600px">
              <Stack mb="12px">
                <Flex align="center" gap="8px" color="neutrals.600">
                  <MapPin size={20} />
                  <Text fontSize="md">{job.location}</Text>
                </Flex>

                <Flex align="center" gap="8px" color="neutrals.600">
                  <Briefcase size={20} />
                  <Text fontSize="md">{getWorkTypeLabel(job.work_type)}</Text>
                </Flex>
              </Stack>

              <Stack>
                <Flex align="center" gap="8px" color="neutrals.600">
                  <CurrencyDollar size={16} />
                  <Text fontSize="md">{formatSalary()}</Text>
                </Flex>

                <Flex align="center" gap="8px" color="neutrals.600">
                  <Clock size={20} />
                  <Text fontSize="md">{getEmploymentTypeLabel(job.employment_type)}</Text>
                </Flex>
              </Stack>
            </Flex>
          </Stack>

          {/* Job Description */}
          <Box mb="40px">
            <Heading fontSize="24px" mb="16px" color="neutrals.900">
              Job Description
            </Heading>
            <RichEditor
              key="job-description"
              content={job.description}
              type="job-description"
              readOnly
            />
          </Box>

          {/* Contact Information */}
          {job.contact_name && (
            <Box mb="24px">
              <Heading fontSize="24px" mb="16px" color="neutrals.900">
                Contact Information
              </Heading>
              <Flex justify="space-between" flexWrap="wrap" mt="8px" maxW="600px">
                <Stack gap="12px">
                  <Text fontSize="16px" color="neutrals.500" fontWeight="medium">
                    Contact Person
                  </Text>
                  <Text fontSize="16px" color="neutrals.500" fontWeight="medium">
                    Role
                  </Text>
                  <Text fontSize="16px" color="neutrals.500" fontWeight="medium">
                    Email
                  </Text>
                  <Text fontSize="md" color="neutrals.500" fontWeight="medium">
                    Phone
                  </Text>
                </Stack>

                <Stack gap="12px">
                  <Text fontSize="16px" color="neutrals.900">
                    {job.contact_name}
                  </Text>
                  <Text fontSize="16px" color="neutrals.900">
                    {job.contact_role}
                  </Text>
                  <Text fontSize="16px" color="neutrals.700">
                    <a href={`mailto:${job.contact_email}`}>{job.contact_email}</a>
                  </Text>
                  <Text fontSize="md" color="neutrals.700">
                    <a href={`tel:${job.contact_phone}`}>{job.contact_phone}</a>
                  </Text>
                </Stack>
              </Flex>
            </Box>
          )}

          <Separator mt="32px" mb="32px" />
          <ApplySection
            agencyId={agencyId!}
            jobId={jobId!}
            inIframeMode={inIframeMode}
            applicationQuestions={job.application_questions || []}
            agency={agency}
          />
        </Stack>

        {/* Hide footer in iframe mode */}
        {!inIframeMode && (
          <Box as="footer" bg="background.primary" h="40px" alignContent="center">
            <Text fontSize="14px" color="neutrals.700" textAlign="center">
              Powered by{' '}
              <Text as="span" color="primary.900" fontWeight="bold">
                <a href="https://www.polarisjobs.com" target="_blank" rel="noreferrer">
                  PolarisJobs
                </a>
              </Text>
            </Text>
          </Box>
        )}
      </Flex>
    </Flex>
  );
}

interface ApplySectionProps {
  agencyId: string;
  jobId: string;
  inIframeMode: boolean;
  applicationQuestions: ApplicationQuestion[];
  agency: Agency | null;
}

interface ApplyFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  linkedin?: string;
}

const ApplySection = ({
  agencyId,
  jobId,
  inIframeMode,
  applicationQuestions,
  agency
}: ApplySectionProps) => {
  const hasVeteransResumeFeature =
    agency?.custom_features.some(
      (f) => f.feature_key === AgencyFeature.VeteransResume && f.is_enabled
    ) ?? false;
  // Multi-step state: 'basic-info', 'preview-resume', or 'questions'
  const [currentStep, setCurrentStep] = useState<'basic-info' | 'preview-resume' | 'questions'>(
    'basic-info'
  );
  const [useMilitaryResume, setUseMilitaryResume] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] =
    useState<JobBoardTranslateVeteransResumeProgress | null>(null);
  const [translatedResume, setTranslatedResume] = useState<Resume | null>(null);
  const [translateError, setTranslateError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [applicationAnswers, setApplicationAnswers] = useState<ApplicationQuestionAnswer[]>([]);
  const fileSizeLimit = MAX_RESUME_FILE_SIZE;

  const hasQuestions = applicationQuestions.length > 0;

  // Iframe height sync
  const syncHeight = useIframeHeightSync();

  // Sync height when step changes
  useEffect(() => {
    if (inIframeMode) {
      syncHeight();
      // Scroll to top when step changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, inIframeMode, syncHeight]);

  // Sync height after translated resume content renders in iframe
  useEffect(() => {
    if (inIframeMode && translatedResume) {
      setTimeout(() => syncHeight(), 0);
    }
  }, [inIframeMode, translatedResume, syncHeight]);

  // Sync height when progress bar appears/disappears in iframe
  useEffect(() => {
    if (inIframeMode) {
      setTimeout(() => syncHeight(), 0);
    }
  }, [inIframeMode, isTranslating, syncHeight]);

  const [provider, setProvider] = useState<Provider>({
    analysis_id: null,
    analysis_name: PromptKey.ResumeExtractor,
    provider: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchProviders = async () => {
      setProvider({ ...defaultProvider, loading: true, error: null });

      try {
        const response = await fetchAPI<AnalysisProvider[]>({
          request: apiGetExtractorProviders
        });

        if (response.success) {
          const providersData = response.data;
          const extractorProvider = providersData.find(
            (p) => p.analysis_name === PromptKey.ResumeExtractor
          );

          if (extractorProvider) {
            setProvider({
              ...extractorProvider,
              loading: false,
              error: null
            });
          } else {
            console.error('No CV extractor provider configured.');
            setProvider({
              ...defaultProvider,
              error:
                'No CV extractor provider configured. Please contact support to enable file upload.'
            });
          }
        } else {
          console.error('Error fetching analysis providers:', response.message);
          setProvider({
            ...defaultProvider,
            error: 'Failed to load file upload options. Please try again later.'
          });
        }
      } catch (error) {
        console.error('Unexpected error fetching analysis providers:', error);
        setProvider({
          ...defaultProvider,
          error: 'Failed to load file upload options. Please try again later.'
        });
      }
    };

    fetchProviders();
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<ApplyFormValues>();

  const onSubmitBasicInfo = handleSubmit(async (data) => {
    if (!resumeFile) {
      setFileError('Resume is required');
      setSubmitError('Resume is required');
      return;
    }

    if (resumeFile.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      setSubmitError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      return;
    }

    if (!data.phone) {
      setSubmitError('Phone number is required');
      return;
    }

    setSubmitError(null);

    // Military mode: translate resume first and show preview
    if (hasVeteransResumeFeature && useMilitaryResume) {
      setIsTranslating(true);
      setTranslateError(null);
      setTranslateProgress(null);
      try {
        const response = await apiJobBoardTranslateVeteransResume({
          agencyId,
          payload: { resume: resumeFile },
          onProgress: (p) => setTranslateProgress(p)
        });
        const isSuccess = response.code === 200 || response.code === 201;
        if (isSuccess && response.data) {
          setTranslatedResume(response.data);
          setCurrentStep('preview-resume');
        } else {
          setTranslateError('Failed to translate resume. Please try again.');
        }
      } catch {
        setTranslateError('Failed to translate resume. Please try again.');
      } finally {
        setIsTranslating(false);
        setTranslateProgress(null);
      }
      return;
    }

    // If there are application questions, move to next step
    if (hasQuestions) {
      setCurrentStep('questions');
      return;
    }

    // Otherwise, submit directly
    await submitApplication(data);
  });

  const submitApplication = async (data: ApplyFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        linkedin: data.linkedin || '',
        resume: resumeFile!,
        application_answers: applicationAnswers.length > 0 ? applicationAnswers : undefined
      };

      const applyRequest =
        hasVeteransResumeFeature && useMilitaryResume
          ? () => apiApplyJobWithVeteransResume({ agencyId, jobId, payload })
          : () => apiApplyJob({ agencyId, jobId, payload });

      const response = await fetchAPI<CandidateWithJobContext>({ request: applyRequest });

      if (response.success) {
        setSubmitSuccess(true);
        reset();
        setResumeFile(null);
        setFileError(null);
        setApplicationAnswers([]);
        setTranslatedResume(null);
        setCurrentStep('basic-info');
        // Notify parent if in iframe
        if (inIframeMode) {
          notifyApplicationSubmitted(true);
        }
      } else {
        console.error('Error submitting application:', response.message);
        setSubmitError('Failed to submit application. Please try again.');
        // Notify parent if in iframe
        if (inIframeMode) {
          notifyApplicationSubmitted(false);
        }
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitError('Failed to submit application. Please try again.');
      // Notify parent if in iframe
      if (inIframeMode) {
        notifyApplicationSubmitted(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError(null);

    if (file.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      return;
    }

    const allowedTypes = provider?.provider
      ? getLlmModelProviderSupportFileTypes(provider.provider)
      : ['.pdf'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    // Normalize allowed types and file extension for more robust matching
    const normalizedAllowedTypes = allowedTypes.map((type) => type.toLowerCase().trim());
    const normalizedFileExtension = fileExtension.toLowerCase().trim();

    if (!normalizedAllowedTypes.includes(normalizedFileExtension)) {
      setFileError(
        `Unsupported file type. Please upload one of the following: ${normalizedAllowedTypes.join(
          ', '
        )}`
      );
      return;
    }

    setResumeFile(file);
  };

  const removeFile = () => {
    setResumeFile(null);
    setFileError(null);
  };

  // Render different steps based on currentStep
  if (currentStep === 'preview-resume' && translatedResume) {
    const candidateName = `${watch('firstName')}_${watch('lastName')}`;
    return (
      <Box>
        <Flex align="center" gap="12px" mb="24px">
          <Box
            cursor="pointer"
            onClick={() => setCurrentStep('basic-info')}
            color="neutrals.600"
            _hover={{ color: 'neutrals.900' }}
          >
            <CaretLeft size={24} />
          </Box>
          <Heading color="neutrals.900">Translated Resume Preview</Heading>
        </Flex>
        <Text fontSize="16px" color="neutrals.600" mb="24px">
          Your military resume has been translated into a civilian-friendly format. Review it below
          before submitting your application.
        </Text>

        <Box mb="32px">
          <Resume resume={translatedResume} />
        </Box>

        {submitError && (
          <Text fontSize="16px" color="error.700" mb="16px">
            {submitError}
          </Text>
        )}

        <Flex gap="12px" justify="space-between">
          <Button
            size="lg"
            py="20px"
            borderRadius="8px"
            bg="primary.900"
            color="neutrals.0"
            _hover={{ bg: 'primary.500' }}
            onClick={() => exportResumeToDocx(translatedResume, `${candidateName} Resume.docx`)}
          >
            <DownloadSimple size={16} />
            Download Resume
          </Button>
          <Flex gap="12px">
            <Button
              variant="outline"
              size="lg"
              borderRadius="8px"
              borderColor="neutrals.300"
              color="neutrals.700"
              onClick={() => setCurrentStep('basic-info')}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              size="lg"
              bg="primary.900"
              color="white"
              borderRadius="8px"
              loading={isSubmitting}
              disabled={isSubmitting}
              _hover={{ bg: 'primary.700' }}
              onClick={() => {
                if (hasQuestions) {
                  setCurrentStep('questions');
                } else {
                  submitApplication(watch());
                }
              }}
            >
              {hasQuestions ? 'Next' : 'Submit Application'}
            </Button>
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (currentStep === 'questions') {
    return (
      <Box>
        <Heading mb="24px" color="neutrals.900">
          Application Questions
        </Heading>
        <Text fontSize="16px" color="neutrals.600" mb="32px">
          Please answer the following questions to complete your application.
        </Text>
        <ApplicationQuestionsForm
          questions={applicationQuestions}
          answers={applicationAnswers}
          onAnswersChange={setApplicationAnswers}
          onBack={() =>
            hasVeteransResumeFeature && useMilitaryResume && translatedResume
              ? setCurrentStep('preview-resume')
              : setCurrentStep('basic-info')
          }
          onSubmit={() => submitApplication(watch())}
          isSubmitting={isSubmitting}
          submitError={submitError}
          inIframeMode={inIframeMode}
          syncHeight={syncHeight}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Heading mb="24px" color="neutrals.900">
        Apply for this Position
      </Heading>

      <form onSubmit={onSubmitBasicInfo}>
        <Stack gap="20px">
          {/* Name Fields */}
          <Flex gap="16px">
            <Field
              label={
                <Box fontSize="16px" fontWeight="medium" color="neutrals.700">
                  <Box as="span" color="error.500">
                    *{' '}
                  </Box>
                  First Name
                </Box>
              }
              invalid={!!errors.firstName}
              errorText={
                <Box fontSize="14px" color="error.700">
                  {errors.firstName?.message}
                </Box>
              }
              flex="1"
            >
              <Input
                size="lg"
                borderRadius="8px"
                placeholder="Enter your first name"
                disabled={isSubmitting}
                {...register('firstName', {
                  required: 'First name is required'
                })}
              />
            </Field>
            <Field
              label={
                <Box fontSize="16px" fontWeight="medium" color="neutrals.700">
                  <Box as="span" color="error.500">
                    *{' '}
                  </Box>
                  Last Name
                </Box>
              }
              invalid={!!errors.lastName}
              errorText={
                <Box fontSize="14px" color="error.700">
                  {errors.lastName?.message}
                </Box>
              }
              flex="1"
            >
              <Input
                size="lg"
                borderRadius="8px"
                placeholder="Enter your last name"
                disabled={isSubmitting}
                {...register('lastName', {
                  required: 'Last name is required'
                })}
              />
            </Field>
          </Flex>

          {/* Email Field */}
          <Field
            label={
              <Box fontSize="16px" fontWeight="medium" color="neutrals.700">
                <Box as="span" color="error.500">
                  *{' '}
                </Box>
                Email
              </Box>
            }
            invalid={!!errors.email}
            errorText={
              <Box fontSize="14px" color="error.700">
                {errors.email?.message}
              </Box>
            }
          >
            <Input
              size="lg"
              borderRadius="8px"
              placeholder="your.email@example.com"
              type="email"
              disabled={isSubmitting}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Invalid email address'
                }
              })}
            />
          </Field>

          {/* Phone Field */}
          <Field
            label={
              <Box fontSize="16px" fontWeight="medium" color="neutrals.700">
                <Box as="span" color="error.500">
                  *{' '}
                </Box>
                Phone Number
              </Box>
            }
            invalid={!!errors.phone}
            errorText={
              <Box fontSize="14px" color="error.700">
                {errors.phone?.message}
              </Box>
            }
          >
            <Box className={errors.phone ? 'phone-input-error' : ''} w="100%">
              <PhoneInput
                country={'us'}
                placeholder="+1 (123) 456-7890"
                value={watch('phone') || ''}
                enableSearch
                disabled={isSubmitting}
                onChange={(phone) => {
                  setValue('phone', phone, { shouldValidate: true });
                  if (phone && phone.length >= 10) {
                    clearErrors('phone');
                  }
                }}
                onBlur={() => {
                  const phoneValue = watch('phone');
                  if (!phoneValue || phoneValue.length < 7) {
                    setError('phone', {
                      type: 'manual',
                      message: 'Please enter a valid phone number'
                    });
                  }
                }}
                inputProps={{
                  name: 'phone',
                  required: true
                }}
              />
            </Box>
          </Field>

          {/* LinkedIn Field */}
          <Field
            label={
              <Box fontSize="16px" fontWeight="medium" color="neutrals.700">
                LinkedIn Profile
              </Box>
            }
            invalid={!!errors.linkedin}
            errorText={
              <Box fontSize="14px" color="error.700">
                {errors.linkedin?.message}
              </Box>
            }
          >
            <Input
              size="lg"
              borderRadius="8px"
              placeholder="https://linkedin.com/in/your-profile"
              disabled={isSubmitting}
              {...register('linkedin', {
                pattern: {
                  value: /^(https?:\/\/)?(www\.)?linkedin\.com\/.+$/,
                  message: 'Please enter a valid LinkedIn URL'
                }
              })}
            />
          </Field>

          {/* Resume Upload */}
          <Box>
            <Flex align="center" justify="space-between" mb="8px">
              <Text fontSize="16px" fontWeight="medium" color="neutrals.700">
                <Box as="span" color="error.500">
                  *{' '}
                </Box>
                Resume/CV
              </Text>
              {hasVeteransResumeFeature && (
                <Flex align="center" gap="8px">
                  <Text
                    fontSize="13px"
                    fontWeight={!useMilitaryResume ? 'semibold' : 'normal'}
                    color={!useMilitaryResume ? 'primary.900' : 'neutrals.500'}
                    cursor="pointer"
                    onClick={() => setUseMilitaryResume(false)}
                  >
                    Standard
                  </Text>
                  <Box
                    w="36px"
                    h="20px"
                    borderRadius="full"
                    bg={useMilitaryResume ? 'primary.900' : 'neutrals.300'}
                    position="relative"
                    onClick={() => !isSubmitting && setUseMilitaryResume((v) => !v)}
                    flexShrink={0}
                    cursor={isSubmitting ? 'not-allowed' : 'pointer'}
                    transition="background 0.2s"
                  >
                    <Box
                      position="absolute"
                      top="2px"
                      left={useMilitaryResume ? '18px' : '2px'}
                      w="16px"
                      h="16px"
                      borderRadius="full"
                      bg="white"
                      transition="left 0.2s"
                    />
                  </Box>
                  <Text
                    fontSize="13px"
                    fontWeight={useMilitaryResume ? 'semibold' : 'normal'}
                    color={useMilitaryResume ? 'primary.900' : 'neutrals.500'}
                    cursor="pointer"
                    onClick={() => setUseMilitaryResume(true)}
                  >
                    Military
                  </Text>
                </Flex>
              )}
            </Flex>
            {hasVeteransResumeFeature && useMilitaryResume && (
              <Text fontSize="13px" color="neutrals.500" mb="8px">
                Your military resume will be automatically translated into a civilian-friendly
                format.
              </Text>
            )}
            {(() => {
              const formats = provider?.provider
                ? getLlmModelProviderSupportFileTypes(provider.provider)
                : ['.pdf'];
              const isSingleFormat = formats.length === 1;
              return (
                <Text fontSize="14px" color="neutrals.500" mb="12px">
                  Supported {isSingleFormat ? 'format' : 'formats'}: {formats.join(', ')} (
                  {getFileSize(fileSizeLimit)} max)
                </Text>
              );
            })()}

            {resumeFile ? (
              <Box display="flex" alignItems="center" width="100%">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  width="120px"
                  height="120px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={fileError ? 'error.500' : 'primary.100'}
                  bg="primary.50"
                >
                  <FileIcon size={48} color="#4e76ff" />
                </Box>
                <Box ml="24px" color="neutrals.900" flex="1">
                  <Box display="flex" alignItems="center" mb="4px">
                    <Box mr="12px">
                      <CheckCircle size={20} color="#4e76ff" weight="fill" />
                    </Box>
                    <Text fontSize="16px" color="neutrals.800" mr="12px">
                      {resumeFile.name}
                    </Text>
                    {!isSubmitting && (
                      <Box cursor="pointer" onClick={removeFile}>
                        <XIcon size={16} color="#545454" />
                      </Box>
                    )}
                  </Box>
                  <Text fontSize="14px" color={fileError ? 'error.700' : 'neutrals.500'}>
                    {getFileSize(resumeFile.size)}
                  </Text>
                  {fileError && (
                    <Text fontSize="14px" color="error.700" mt="4px">
                      {fileError}
                    </Text>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                position="relative"
                width="100%"
                height="160px"
                border="2px dashed"
                borderColor={fileError ? 'error.300' : 'neutrals.300'}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                _hover={{ borderColor: fileError ? 'error.400' : 'neutrals.400' }}
                bg={fileError ? 'error.50' : 'neutrals.50'}
              >
                <input
                  type="file"
                  accept={
                    provider?.provider
                      ? getLlmModelProviderSupportFileTypes(provider.provider).join(',')
                      : '.pdf'
                  }
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                />
                <Box display="flex" flexDirection="column" alignItems="center">
                  <UploadSimple size={48} color={fileError ? '#dc2626' : '#404040'} />
                  <Text textAlign="center" color="neutrals.900" mt="8px">
                    Drop file here or{' '}
                    <Box as="span" color={fileError ? 'error.600' : 'primary.900'}>
                      click to upload
                    </Box>
                  </Text>
                  {fileError && (
                    <Text fontSize="14px" color="error.700" mt="8px">
                      {fileError}
                    </Text>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {submitError && (
            <Text fontSize="16px" color="error.700">
              {submitError}
            </Text>
          )}

          {submitSuccess && (
            <Box
              bg="success.50"
              border="1px solid"
              borderColor="success.500"
              borderRadius="12px"
              p="16px"
              mb="24px"
            >
              <Flex align="center" gap="12px">
                <Box color="success.500">
                  <CheckCircle size={24} weight="fill" />
                </Box>
                <Box>
                  <Text fontSize="16px" fontWeight="semibold" color="success.500">
                    Application Submitted Successfully!
                  </Text>
                  <Text fontSize="14px" color="neutrals.700">
                    We'll review your application and get back to you soon.
                  </Text>
                </Box>
              </Flex>
            </Box>
          )}

          {translateError && (
            <Text fontSize="16px" color="error.700">
              {translateError}
            </Text>
          )}

          {isTranslating && (
            <Box>
              <Flex align="center" gap="8px" mb="12px">
                <Box color="warning.500">
                  <Warning size={18} weight="fill" />
                </Box>
                <Text fontSize="14px" color="warning.500">
                  Please don't refresh the page. This translation typically takes 30 seconds.
                </Text>
              </Flex>
              <Flex justify="space-between" mb="6px">
                <Text fontSize="14px" color="neutrals.500">
                  {translateProgress?.step ?? 'Translating military resume...'}
                </Text>
                <Text fontSize="14px" fontWeight="semibold" color="primary.900">
                  {translateProgress?.percentage ?? 0}%
                </Text>
              </Flex>
              <Box w="100%" h="8px" bg="neutrals.200" borderRadius="full" overflow="hidden">
                <Box
                  h="100%"
                  bg="primary.900"
                  borderRadius="full"
                  w={`${translateProgress?.percentage ?? 0}%`}
                  transition="width 0.3s ease"
                />
              </Box>
            </Box>
          )}

          {/* Submit/Continue Button */}
          <Button
            type="submit"
            alignSelf="center"
            p="24px 48px"
            size="lg"
            bg="primary.900"
            color="white"
            borderRadius="8px"
            mt="8px"
            w="fit-content"
            fontSize="16px"
            fontWeight="medium"
            loading={isSubmitting || isTranslating}
            disabled={isSubmitting || isTranslating}
            _hover={{ bg: 'primary.700' }}
          >
            {hasVeteransResumeFeature && useMilitaryResume
              ? 'Translate Military Resume'
              : hasQuestions
                ? 'Next'
                : 'Submit Application'}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

// Application Questions Form Component
interface ApplicationQuestionsFormProps {
  questions: ApplicationQuestion[];
  answers: ApplicationQuestionAnswer[];
  onAnswersChange: (answers: ApplicationQuestionAnswer[]) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  inIframeMode: boolean;
  syncHeight: () => void;
}

const ApplicationQuestionsForm = ({
  questions,
  answers,
  onAnswersChange,
  onBack,
  onSubmit,
  isSubmitting,
  submitError,
  inIframeMode,
  syncHeight
}: ApplicationQuestionsFormProps) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleAnswerChange = (questionId: string, answer: string) => {
    const question = questions.find((q) => q.question_id === questionId);
    if (!question) return;

    const newAnswers = [...answers];
    const existingIndex = newAnswers.findIndex((a) => a.question_id === questionId);

    const answerObj: ApplicationQuestionAnswer = {
      question_id: questionId,
      question: question.question,
      question_type: question.question_type,
      options: question.options,
      required: question.required,
      answer
    };

    if (existingIndex >= 0) {
      newAnswers[existingIndex] = answerObj;
    } else {
      newAnswers.push(answerObj);
    }

    onAnswersChange(newAnswers);

    // Clear error for this question if it was set
    if (errors[questionId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }

    // Sync height after answer change in iframe mode
    if (inIframeMode) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => syncHeight(), 0);
    }
  };

  const getAnswerValue = (questionId: string): string => {
    const answer = answers.find((a) => a.question_id === questionId);
    return answer?.answer || '';
  };

  const validateAndSubmit = () => {
    const newErrors: { [key: string]: string } = {};

    // Check all required questions are answered
    questions.forEach((question) => {
      if (question.required) {
        const answer = getAnswerValue(question.question_id);
        if (!answer || answer.trim() === '') {
          newErrors[question.question_id] = 'This question is required';
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Sync height after errors are shown in iframe mode
      if (inIframeMode) {
        setTimeout(() => syncHeight(), 0);
      }
      return;
    }

    onSubmit();
  };

  return (
    <Stack gap="32px">
      {questions.map((question, index) => (
        <Box key={question.question_id}>
          <Text fontSize="16px" fontWeight="medium" color="neutrals.700" mb="12px">
            {index + 1}. {question.question}
            {question.required && (
              <Box as="span" color="error.500" ml="4px">
                *
              </Box>
            )}
          </Text>

          {question.question_type === 'text' && (
            <Field
              invalid={!!errors[question.question_id]}
              errorText={
                <Box fontSize="14px" color="error.700">
                  {errors[question.question_id]}
                </Box>
              }
            >
              <Input
                size="lg"
                borderRadius="8px"
                placeholder="Enter your answer"
                value={getAnswerValue(question.question_id)}
                onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                disabled={isSubmitting}
              />
            </Field>
          )}

          {question.question_type === 'yes_no' && (
            <Stack gap="12px">
              <Flex gap="16px">
                <Button
                  variant={getAnswerValue(question.question_id) === 'yes' ? 'solid' : 'outline'}
                  onClick={() => handleAnswerChange(question.question_id, 'yes')}
                  disabled={isSubmitting}
                  bg={
                    getAnswerValue(question.question_id) === 'yes' ? 'primary.900' : 'transparent'
                  }
                  color={getAnswerValue(question.question_id) === 'yes' ? 'white' : 'neutrals.700'}
                  border="1px solid"
                  borderRadius="8px"
                  borderColor="neutrals.300"
                  fontSize="16px"
                  _hover={{
                    bg:
                      getAnswerValue(question.question_id) === 'yes' ? 'primary.700' : 'neutrals.50'
                  }}
                >
                  Yes
                </Button>
                <Button
                  variant={getAnswerValue(question.question_id) === 'no' ? 'solid' : 'outline'}
                  onClick={() => handleAnswerChange(question.question_id, 'no')}
                  disabled={isSubmitting}
                  bg={getAnswerValue(question.question_id) === 'no' ? 'primary.900' : 'transparent'}
                  color={getAnswerValue(question.question_id) === 'no' ? 'white' : 'neutrals.700'}
                  border="1px solid"
                  borderRadius="8px"
                  borderColor="neutrals.300"
                  fontSize="16px"
                  _hover={{
                    bg:
                      getAnswerValue(question.question_id) === 'no' ? 'primary.700' : 'neutrals.50'
                  }}
                >
                  No
                </Button>
              </Flex>
              {errors[question.question_id] && (
                <Text fontSize="14px" color="error.700">
                  {errors[question.question_id]}
                </Text>
              )}
            </Stack>
          )}

          {question.question_type === 'multiple_choice' && question.options && (
            <Stack gap="12px">
              {question.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleAnswerChange(question.question_id, option)}
                  disabled={isSubmitting}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid',
                    borderColor:
                      getAnswerValue(question.question_id) === option ? '#4e76ff' : '#d1d5db',
                    borderRadius: '8px',
                    backgroundColor:
                      getAnswerValue(question.question_id) === option ? '#eff3ff' : 'transparent',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                >
                  <Text
                    fontSize="16px"
                    color={
                      getAnswerValue(question.question_id) === option
                        ? 'primary.900'
                        : 'neutrals.700'
                    }
                    fontWeight={
                      getAnswerValue(question.question_id) === option ? 'medium' : 'normal'
                    }
                  >
                    {option}
                  </Text>
                </button>
              ))}
              {errors[question.question_id] && (
                <Text fontSize="14px" color="error.700">
                  {errors[question.question_id]}
                </Text>
              )}
            </Stack>
          )}
        </Box>
      ))}

      {submitError && (
        <Text fontSize="16px" color="error.700">
          {submitError}
        </Text>
      )}

      <Flex gap="16px" justify="center" mt="24px">
        <Button
          variant="outline"
          size="lg"
          borderRadius="8px"
          onClick={onBack}
          disabled={isSubmitting}
          borderColor="neutrals.300"
          color="neutrals.700"
          _hover={{ bg: 'neutrals.50' }}
        >
          Back
        </Button>
        <Button
          size="lg"
          bg="primary.900"
          color="white"
          borderRadius="8px"
          onClick={validateAndSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
          _hover={{ bg: 'primary.700' }}
          p="24px 48px"
        >
          Submit Application
        </Button>
      </Flex>
    </Stack>
  );
};

export default JobDetails;
